import {
    faCheck,
    faEdit,
    faFileLines,
    faGear,
    faPercent,
    faTicket,
    faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { DropdownAtom } from "../../../components/atoms/atom";
import ButtonAtom from "../../../components/atoms/button/button";
import {
    DEFAULT_VAT_PERCENTAGE,
    GERMAN_VAT_OPTIONS,
} from "../../../constants/vat-config";
import useProjectPermission from "../../../hooks/useProjectPermission";
import { useUpdateProjectSettingsMutation } from "../../../store/services/projectsService";
import { showToast } from "../../../utils/method";
import { PROJECT_AREA, PROJECT_ACTION } from "../../../constant/projectPermissions";
import "./project-overview.scss";

const ProjectSettingsRow = ({ projectDetails, templates }) => {
    const { t } = useTranslation();

    const { loading: templatesLoading } = useSelector(
        (state) => state.templates,
    );
    const [updateProjectSettings, { isLoading: settingsUpdateLoading }] =
        useUpdateProjectSettingsMutation();

    const { can, isLoading: permissionLoading } = useProjectPermission();
    const canEditProject = !permissionLoading && can(PROJECT_AREA.OVERVIEW, PROJECT_ACTION.EDIT);

    const [isEditing, setIsEditing] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState("");
    const [selectedVat, setSelectedVat] = useState(DEFAULT_VAT_PERCENTAGE);
    const [requireTicketApproval, setRequireTicketApproval] = useState(false);

    const isVATDisabled = projectDetails?.invoiceCounts?.invoiceFinalized > 0;
    // Ticket approval is a local-project (non-CAPMO) feature for now.
    const isLocalProject = !projectDetails?.capmoId;

    useEffect(() => {
        if (projectDetails) {
            setSelectedTemplateId(projectDetails.templateId || "");
            setSelectedVat(projectDetails.vat ?? DEFAULT_VAT_PERCENTAGE);
            setRequireTicketApproval(
                projectDetails.requireTicketApproval ?? false,
            );
        }
    }, [projectDetails]);

    const templateOptions = (templates || []).map((template) => ({
        value: template.id,
        label: template.name,
    }));

    const selectedTemplateName = (() => {
        if (!projectDetails?.templateId)
            return t("projects.no-template-selected");
        return (
            projectDetails.template?.name ||
            templates?.find((tpl) => tpl.id === projectDetails.templateId)
                ?.name ||
            t("projects.template-not-found")
        );
    })();

    const handleEdit = () => setIsEditing(true);

    const handleSave = async () => {
        try {
            await updateProjectSettings({
                projectId: projectDetails.id,
                settings: {
                    templateId: selectedTemplateId || null,
                    vat: selectedVat,
                    // Only local projects manage ticket approval for now.
                    ...(isLocalProject ? { requireTicketApproval } : {}),
                },
            }).unwrap();

            setIsEditing(false);
            showToast(t("projects.settings-updated-successfully"), "success");
        } catch (error) {
            showToast(
                error?.data?.message ||
                    error?.message ||
                    t("projects.settings-update-failed"),
                "error",
            );
        }
    };

    const handleCancel = () => {
        setSelectedTemplateId(projectDetails?.templateId || "");
        setSelectedVat(projectDetails?.vat ?? DEFAULT_VAT_PERCENTAGE);
        setRequireTicketApproval(
            projectDetails?.requireTicketApproval ?? false,
        );
        setIsEditing(false);
    };

    return (
        <div className="po-card">
            <div className="po-card-header">
                <div className="po-card-title">
                    <FontAwesomeIcon icon={faGear} />
                    {t("projects.overview.settings-configuration")}
                </div>
                <div className="po-card-header-actions">
                    {!isEditing
                        ? canEditProject && (
                              <ButtonAtom
                                  type="button"
                                  className="btn btn-secondary"
                                  onClick={handleEdit}
                                  disabled={settingsUpdateLoading}
                              >
                                  <FontAwesomeIcon
                                      icon={faEdit}
                                      className="me-2"
                                  />
                                  {t("common.edit")}
                              </ButtonAtom>
                          )
                        : (
                              <>
                                  <ButtonAtom
                                      type="button"
                                      className="btn btn-primary"
                                      onClick={handleSave}
                                      disabled={settingsUpdateLoading}
                                  >
                                      {settingsUpdateLoading ? (
                                          <span
                                              className="spinner-border spinner-border-sm me-2"
                                              role="status"
                                              aria-hidden="true"
                                          ></span>
                                      ) : (
                                          <FontAwesomeIcon
                                              icon={faCheck}
                                              className="me-2"
                                          />
                                      )}
                                      {t("common.save")}
                                  </ButtonAtom>
                                  <ButtonAtom
                                      type="button"
                                      className="btn btn-secondary"
                                      onClick={handleCancel}
                                      disabled={settingsUpdateLoading}
                                  >
                                      <FontAwesomeIcon
                                          icon={faTimes}
                                          className="me-2"
                                      />
                                      {t("common.cancel")}
                                  </ButtonAtom>
                              </>
                          )}
                </div>
            </div>

            <div className="po-card-body">
                <div className="po-settings-body">
                    {/* Template */}
                    <div className="po-setting-row">
                        <div className="po-setting-icon">
                            <FontAwesomeIcon icon={faFileLines} />
                        </div>
                        <div className="po-setting-text">
                            <span className="po-setting-label">
                                {t("projects.template")}
                            </span>
                        </div>
                        <div className="po-setting-control">
                            {!isEditing ? (
                                <span className="po-setting-value">
                                    {selectedTemplateName}
                                </span>
                            ) : (
                                <div className="po-dropdown-200">
                                    <DropdownAtom
                                        value={selectedTemplateId}
                                        options={templateOptions}
                                        onChange={(e) =>
                                            setSelectedTemplateId(e.target.value)
                                        }
                                        placeholder={t(
                                            "projects.select-template",
                                        )}
                                        disabled={
                                            templatesLoading?.templates ||
                                            settingsUpdateLoading
                                        }
                                        loading={templatesLoading?.templates}
                                        isClearable
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* VAT */}
                    <div className="po-setting-row">
                        <div className="po-setting-icon">
                            <FontAwesomeIcon icon={faPercent} />
                        </div>
                        <div className="po-setting-text">
                            <span className="po-setting-label">
                                {t("projects.vat")}
                            </span>
                        </div>
                        <div className="po-setting-control">
                            {!isEditing ? (
                                <span className="po-setting-value">
                                    {projectDetails?.vat ??
                                        DEFAULT_VAT_PERCENTAGE}
                                    %
                                </span>
                            ) : (
                                <div className="po-dropdown-120">
                                    <DropdownAtom
                                        value={selectedVat}
                                        options={GERMAN_VAT_OPTIONS}
                                        onChange={(e) =>
                                            setSelectedVat(
                                                Number(e.target.value),
                                            )
                                        }
                                        placeholder={t("projects.select-vat")}
                                        disabled={
                                            isVATDisabled ||
                                            settingsUpdateLoading
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Ticket approval - local projects only */}
                    {isLocalProject && (
                        <div className="po-setting-row">
                            <div className="po-setting-icon">
                                <FontAwesomeIcon icon={faTicket} />
                            </div>
                            <div className="po-setting-text">
                                <span className="po-setting-label">
                                    {t(
                                        "projects.settings.require-ticket-approval",
                                    )}
                                </span>
                                <span className="po-setting-hint">
                                    {t(
                                        "projects.settings.require-ticket-approval-hint",
                                    )}
                                </span>
                            </div>
                            <div className="po-setting-control">
                                {!isEditing ? (
                                    <span
                                        className={`po-toggle-state ${
                                            requireTicketApproval
                                                ? "po-toggle--on"
                                                : "po-toggle--off"
                                        }`}
                                    >
                                        <span className="po-toggle-dot" />
                                        {requireTicketApproval
                                            ? t("projects.settings.enabled")
                                            : t("projects.settings.disabled")}
                                    </span>
                                ) : (
                                    <Form.Check
                                        type="switch"
                                        id="require-ticket-approval-switch"
                                        className="d-inline-flex align-items-center"
                                        checked={requireTicketApproval}
                                        onChange={(e) =>
                                            setRequireTicketApproval(
                                                e.target.checked,
                                            )
                                        }
                                        disabled={settingsUpdateLoading}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProjectSettingsRow;
